import { useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { usePaymentApi } from 'api/payment/use-payment-api';
import * as appSelectors from 'app/state/app/app-selectors';
import { FLOWS } from 'common/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePaymentUIState } from '../../use-payment-ui-state';

export function useStripePaymentUIState({ email, signatory }) {
  const stripe = useStripe();
  const elements = useElements();

  const paymentUIState = usePaymentUIState({ email, signatory });

  const { shouldLockPayments, runWithUILockAndExceptionHandling, isPaymentFeesReady } = paymentUIState;

  const isPayButtonDisabled = useMemo(
    () => shouldLockPayments || !stripe || !elements || !isPaymentFeesReady,
    [elements, shouldLockPayments, stripe, isPaymentFeesReady]
  );

  const runWithStripeUILockAndExceptionHandling = useCallback(
    async (runnable) => {
      if (!stripe || !elements) return;
      return runWithUILockAndExceptionHandling(runnable);
    },
    [elements, runWithUILockAndExceptionHandling, stripe]
  );

  return {
    ...paymentUIState,
    stripe,
    elements,
    isPayButtonDisabled,
    runWithStripeUILockAndExceptionHandling,
  };
}

export function useDeckardStripe() {
  const [stripe, setStripe] = useState(null);
  const { getStripePublicKey } = usePaymentApi();
  const activeFlow = useSelector(appSelectors.activeFlow);
  const isTotFlow = activeFlow === FLOWS.PAY_TOT;

  useEffect(() => {
    (async () => {
      const { data } = await getStripePublicKey();
      const totKey = data?.tot?.stripePublishableKey;
      const stripePublishableKey = isTotFlow && totKey ? totKey : data?.stripePublishableKey;
      const stripe = await loadStripe(stripePublishableKey);
      setStripe(stripe);
    })();
  }, [getStripePublicKey, isTotFlow]);

  return stripe;
}
